import type { RequestTypeVersionFullDTO } from '@kfolks/types'
import { canonicalStringify } from '../json-canonicalize'

/**
 * Per design Q6 (resolved): the History panel renders an at-a-glance coarse
 * summary on each row. Clicking the summary opens the full field-level diff
 * (the deeper structure produced by `deep-diff.ts`). This file produces the
 * coarse summary only.
 *
 * The summary compares row N to row N-1 across the three top-level authored
 * fields. We compare via canonicalized-JSON equality (the same canonicalize
 * helper the builders use on save) so key-order trivia doesn't surface as a
 * spurious "changed" — two payloads that round-trip byte-equivalent on save
 * read as `unchanged` here.
 *
 * For `workflowConfig` we additionally surface a step-count delta when the
 * `steps[]` length differs — purely informational; admins use the full diff
 * for the actual changes.
 */

export interface CoarseDiffSummary {
	formSchema: 'unchanged' | 'changed'
	workflowConfig: WorkflowConfigDiff
	summaryTemplate: 'unchanged' | 'changed' | 'added' | 'removed'
}

export type WorkflowConfigDiff = { kind: 'unchanged' } | { kind: 'changed'; stepCountDelta: number | null }

/**
 * Sentinel passed for the v1 row: there is no previous version to diff
 * against. Callers render "Initial version" instead of a diff summary.
 */
export const NO_PREVIOUS_VERSION = Symbol('no-previous-version')
export type CoarseDiffInput = RequestTypeVersionFullDTO | typeof NO_PREVIOUS_VERSION

export function computeCoarseDiff(
	current: RequestTypeVersionFullDTO,
	previous: CoarseDiffInput,
): CoarseDiffSummary | null {
	if (previous === NO_PREVIOUS_VERSION) return null

	return {
		formSchema: payloadEquals(current.formSchema, previous.formSchema) ? 'unchanged' : 'changed',
		workflowConfig: diffWorkflowConfig(current.workflowConfig, previous.workflowConfig),
		summaryTemplate: diffSummaryTemplate(current.summaryTemplate, previous.summaryTemplate),
	}
}

function payloadEquals(a: unknown, b: unknown): boolean {
	return canonicalStringify(a) === canonicalStringify(b)
}

function diffWorkflowConfig(current: unknown, previous: unknown): WorkflowConfigDiff {
	if (payloadEquals(current, previous)) return { kind: 'unchanged' }
	const currentSteps = stepsLength(current)
	const previousSteps = stepsLength(previous)
	if (currentSteps === null || previousSteps === null) {
		return { kind: 'changed', stepCountDelta: null }
	}
	return { kind: 'changed', stepCountDelta: currentSteps - previousSteps }
}

function stepsLength(config: unknown): number | null {
	if (!config || typeof config !== 'object') return null
	const steps = (config as { steps?: unknown }).steps
	return Array.isArray(steps) ? steps.length : null
}

function diffSummaryTemplate(
	current: string | null,
	previous: string | null,
): 'unchanged' | 'changed' | 'added' | 'removed' {
	if (current === previous) return 'unchanged'
	if (previous === null && current !== null) return 'added'
	if (current === null && previous !== null) return 'removed'
	return 'changed'
}
